import React, { useEffect, useMemo } from 'react';
import MenuItem from '@components/menu/MenuItem.tsx';
import { MenuItemModel } from '@components/menu/types.ts';
import { useLocalStorage } from '@utils/utils.tsx';
import useActiveRoute from '@hooks/useActiveRoute/useActiveRoute.tsx';
import { useMenuMultiOpen } from '@app/layout/menuAccordionStore.ts';

interface ComponentProps {
  /** Render as the icon rail (labels faded out, sub-menus hidden). */
  smallMenu?: boolean;
  menu: MenuItemModel[];
  /** Key under which the open groups are remembered. */
  localStorageKey: string;
  /** Called when the user navigates, so a hover overlay can close itself. */
  onNavigate?: () => void;
}

const groupKey = (item: MenuItemModel, index: number) => item.id ?? item.dataTestId ?? String(index);

const Menu: React.FC<ComponentProps> = ({ smallMenu, menu, localStorageKey, onNavigate }) => {
  // Open groups, remembered by stable key rather than by array index so reordering the menu cannot
  // open the wrong group. By default only one stays open at a time; the profile switch below lets a
  // user keep several expanded instead.
  const [stored, setStored] = useLocalStorage<string[]>(localStorageKey, []);
  const multiOpen = useMenuMultiOpen();
  const { score } = useActiveRoute();

  // Tolerate the single-key shape this setting previously wrote.
  const openGroups = useMemo(() => (Array.isArray(stored) ? stored : typeof stored === 'string' ? [stored] : []), [stored]);

  // Resolve the active entry once for the whole menu and keep only the longest match, so
  // /customer/expedition/123 highlights the detail's own entry and not every prefix of it.
  const activeMatch = useMemo(() => {
    let best = { groupIndex: -1, subIndex: -1, score: -1 };
    menu.forEach((item, groupIndex) => {
      const own = score(item.url);
      if (own > best.score) best = { groupIndex, subIndex: -1, score: own };
      if (!Array.isArray(item.subMenu)) return;
      item.subMenu.forEach((sub, subIndex) => {
        const s = score(sub.url);
        if (s > best.score) best = { groupIndex, subIndex, score: s };
      });
    });
    return best;
  }, [menu, score]);

  const activeGroupId = activeMatch.groupIndex >= 0 ? groupKey(menu[activeMatch.groupIndex], activeMatch.groupIndex) : null;

  // The group holding the current route opens itself. It deliberately stays open while the sidebar
  // is collapsed, so re-expanding shows the current category without another click. In single mode
  // it also closes whatever else was open; in multi mode it is simply added.
  useEffect(() => {
    if (!activeGroupId) return;
    if (openGroups.includes(activeGroupId)) {
      // Turning the switch back off collapses the extras down to the current section.
      if (!multiOpen && openGroups.length > 1) setStored([activeGroupId]);
      return;
    }
    setStored(multiOpen ? [...openGroups, activeGroupId] : [activeGroupId]);
  }, [activeGroupId, multiOpen]);

  const toggleOpenMenu = (key: string) => {
    if (openGroups.includes(key)) {
      setStored(openGroups.filter(k => k !== key));
      return;
    }
    setStored(multiOpen ? [...openGroups, key] : [key]);
  };

  // No filtering here — each MenuItem decides its own (and its sub-items') visibility from its
  // secured config / render flag. SecuredArea hides it in normal mode and reveals it in security mode.
  return (
    <ul>
      {menu.map((item, index) => {
        const key = groupKey(item, index);
        return (
          <MenuItem
            render={item.render}
            secured={item.secured}
            key={key}
            title={item.title}
            note={item.note}
            icon={item.icon}
            subMenu={item.subMenu}
            mode={item.mode}
            elementCount={item.elementCount}
            attention={item.attention}
            smallMenu={smallMenu}
            url={item.url}
            active={index === activeMatch.groupIndex}
            activeSubIndex={index === activeMatch.groupIndex ? activeMatch.subIndex : -1}
            isOpen={openGroups.includes(key)}
            onNavigate={onNavigate}
            onClick={() => toggleOpenMenu(key)}
            dataTestId={item.dataTestId}
          />
        );
      })}
    </ul>
  );
};

export default Menu;
